import React, { Component, FC, ReactNode } from 'react';
import { Alert } from '@mui/material';
import type { GroupedSummary } from '../healthWalletConstants';

// ============================================================================
// The section framework (agreed architecture, 2026-07-12): a tab's content is
// a CONFIG-DRIVEN list of sections (tabSections.ts registry — common sections
// shared by reference, tab-specific ones listed once). TabRenderer is a thin
// sequencer; each section is a SELF-FETCHING component (it owns its slice
// hooks — RTK dedupes, so shared reads cost one fetch) receiving the same
// SectionContext + its configured props. A section that throws blanks ONLY
// itself (SectionBoundary), not the tab — same fail-soft philosophy as the
// AI-insight box.
// ============================================================================

export interface SectionContext {
  tab: string;                              // which pill this section renders under
  openDetail: (g: GroupedSummary) => void;  // shared drill-down dialog (owned by the renderer)
  askPolly?: (q: string) => void;           // AI seam, forwarded from the shell
}

export type SectionProps = SectionContext & Record<string, unknown>;

export interface SectionDef {
  key: string;
  Component: FC<any>;
  props?: Record<string, unknown>;
}

// Per-section error boundary — one broken section must not take down the tab.
class SectionBoundary extends Component<{ children: ReactNode; sectionKey: string }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(err: unknown) { console.error(`[RendererV1] section "${this.props.sectionKey}" failed:`, err); }
  render() {
    if (this.state.failed) {
      return <Alert severity="warning" sx={{ borderRadius: 2, my: 1 }}>This section couldn’t be displayed.</Alert>;
    }
    return this.props.children;
  }
}

export const TabRenderer: FC<{ sections: SectionDef[]; ctx: SectionContext }> = ({ sections, ctx }) => (
  <>
    {sections.map((s) => (
      <SectionBoundary key={s.key} sectionKey={s.key}>
        <s.Component {...ctx} {...(s.props ?? {})} />
      </SectionBoundary>
    ))}
  </>
);
